/**
 * Blocks standalone checkout of gift / cross-sell add-on products.
 *
 * These items are only meant to be attached to valid campaign orders
 * (e.g. EasyGift FREEBOOK ebook). If the cart contains only add-ons,
 * clear them so customers cannot place add-on-only paid orders.
 *
 * Product IDs:
 * - 8139201183859: Ebook (EasyGift FREEBOOK gift). May reach checkout
 *   only as a $0 line beside a real campaign.
 * - 8269090291827: Apple Music Promotion (checkout cross-sell)
 * - 8168119238771: Campaign Upgrade (checkout cross-sell)
 */
(function () {
  const EBOOK_PRODUCT_ID = 8139201183859;
  const ADDON_ONLY_PRODUCT_IDS = [EBOOK_PRODUCT_ID, 8269090291827, 8168119238771];

  const state = {
    enforcing: false,
    lastClearedAt: 0,
    mutationTimer: null,
    mutationSequence: 0,
    checkoutChecking: false,
    approvedCheckoutControls: new WeakSet(),
    approvedCheckoutForms: new WeakSet(),
  };

  function getCartUrls() {
    const routes = window.routes || {};
    return {
      cart: routes.cart_url || '/cart',
      update: routes.cart_update_url || '/cart/update.js',
      clear: '/cart/clear.js',
    };
  }

  function isAddonProductId(productId) {
    return ADDON_ONLY_PRODUCT_IDS.includes(Number(productId));
  }

  function isAddonOnlyCart(items) {
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.every((item) => isAddonProductId(item.product_id));
  }

  function isPaidEbookLine(item) {
    return Number(item && item.product_id) === EBOOK_PRODUCT_ID && Number(item.final_line_price) > 0;
  }

  function hasPaidEbook(items) {
    return Array.isArray(items) && items.some(isPaidEbookLine);
  }

  async function fetchCart() {
    const response = await fetch((getCartUrls().cart || '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  function isCartMutationRequest(input, init) {
    const rawUrl = typeof input === 'string' ? input : input && input.url;
    if (!rawUrl) return false;

    try {
      const pathname = new URL(rawUrl, window.location.origin).pathname;
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const ajaxMutation = /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(pathname);
      const cartFormMutation = /\/cart\/?$/.test(pathname) && method !== 'GET' && method !== 'HEAD';
      return ajaxMutation || cartFormMutation;
    } catch (error) {
      console.error('GPM addon guard could not parse cart mutation URL:', error);
      return false;
    }
  }

  function getMutationSettleDelay(input) {
    const rawUrl = typeof input === 'string' ? input : input && input.url;
    if (!rawUrl) return 0;

    try {
      const url = new URL(rawUrl, window.location.origin);
      return url.searchParams.get('_easygift_internal') === 'true' ? 5000 : 0;
    } catch (error) {
      console.error('GPM addon guard could not parse mutation timing URL:', error);
      return 0;
    }
  }

  function scheduleMutationCheck(delay) {
    window.clearTimeout(state.mutationTimer);
    const wait = Number(delay) || 0;
    // EasyGift can add the ebook before its discount lands. Drop an
    // add-on-only cart immediately. A priced ebook gets the short wait,
    // then it is removed if it is still not free.
    const immediate = enforceAddonOnlyRestriction({
      reload: wait === 0,
      stripPaidEbook: wait === 0,
    });
    if (wait > 0) {
      state.mutationTimer = window.setTimeout(function () {
        enforceAddonOnlyRestriction({ reload: true, stripPaidEbook: true });
      }, wait);
    }
    return immediate;
  }

  function observeFetchCartMutations() {
    if (window.__gpmAddonGuardFetchObserved) return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const internalMutation = state.enforcing;
      const observeMutation = !internalMutation && isCartMutationRequest(input, init);
      const mutationSequence = observeMutation ? ++state.mutationSequence : 0;
      if (observeMutation) window.clearTimeout(state.mutationTimer);
      const request = originalFetch(input, init);

      if (!observeMutation) return request;

      return request.then(function (response) {
        if (response.ok && mutationSequence === state.mutationSequence) {
          return Promise.resolve(scheduleMutationCheck(getMutationSettleDelay(input))).then(function () {
            return response;
          });
        }
        return response;
      }).catch(function (error) {
        console.error('GPM addon guard could not observe cart fetch:', error);
        throw error;
      });
    };
    window.__gpmAddonGuardFetchObserved = true;
  }

  function observeXhrCartMutations() {
    if (window.__gpmAddonGuardXhrObserved || typeof window.XMLHttpRequest !== 'function') return;

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__gpmCartMutation = isCartMutationRequest(url, { method });
      this.__gpmCartMutationDelay = getMutationSettleDelay(url);
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function () {
      const internalMutation = state.enforcing;
      const observeMutation = !internalMutation && this.__gpmCartMutation;
      const mutationSequence = observeMutation ? ++state.mutationSequence : 0;
      if (observeMutation) {
        window.clearTimeout(state.mutationTimer);
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300 && mutationSequence === state.mutationSequence) {
            scheduleMutationCheck(this.__gpmCartMutationDelay);
          }
        });
      }
      return originalSend.apply(this, arguments);
    };

    window.__gpmAddonGuardXhrObserved = true;
  }

  async function clearAddonItems(items) {
    const urls = getCartUrls();
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });

    const response = await fetch(urls.update, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      // Fallback: clear entire cart when update fails
      await fetch(urls.clear, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });
      return { item_count: 0, items: [] };
    }

    return response.json();
  }

  async function enforceAddonOnlyRestriction(options) {
    const opts = options || {};
    if (state.enforcing) return null;
    if (Date.now() - state.lastClearedAt < 1000) return null;
    // Buy again writes the cart itself and reloads at the end; stripping and
    // reloading mid-restore aborts its /cart/change.js and leaves the cart wrong.
    if (window.__gpmBuyAgainRestoring) return null;

    state.enforcing = true;
    try {
      const cart = opts.cart || (await fetchCart());
      const items = Array.isArray(cart.items) ? cart.items : [];

      // Restore cleanup: drop a paid ebook, never clear the cart.
      if (opts.stripPaidEbookOnly) {
        if (!hasPaidEbook(items)) return cart;
        state.lastClearedAt = Date.now();
        return clearAddonItems(items.filter(isPaidEbookLine));
      }

      // Legacy protection: free gift left alone (discounted to $0)
      const isZeroTotalGiftCart = cart.item_count > 0 && Number(cart.total_price) === 0;

      if (!isAddonOnlyCart(items) && !isZeroTotalGiftCart) {
        if (!(opts.stripPaidEbook && hasPaidEbook(items))) {
          return cart;
        }

        state.lastClearedAt = Date.now();
        const removed = await clearAddonItems(items.filter(isPaidEbookLine));
        if (opts.reload !== false) {
          window.location.reload();
        }
        return removed;
      }

      state.lastClearedAt = Date.now();
      const cleared = isAddonOnlyCart(items)
        ? await clearAddonItems(items)
        : await fetch(getCartUrls().clear, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
          }).then(() => ({ item_count: 0, items: [] }));

      if (opts.reload !== false) {
        const path = window.location.pathname || '';
        if (path === '/cart' || path === '/checkout' || path.indexOf('/checkouts/') === 0) {
          window.location.replace('/');
          return cleared;
        }
        window.location.reload();
      }

      return cleared;
    } catch (error) {
      console.error('GPM addon cart guard failed:', error);
      return null;
    } finally {
      state.enforcing = false;
    }
  }

  function getCheckoutControl(target) {
    if (!target || !target.closest) return null;
    return target.closest(
      '[name="checkout"], .cart__checkout-button, #checkout, #CartDrawer-Checkout, [href="/checkout"], [href*="/checkout"], .shopify-payment-button__button'
    );
  }

  function getControlForm(control) {
    if (!control) return null;
    if (control.form) return control.form;
    const formId = control.getAttribute && control.getAttribute('form');
    if (formId) return document.getElementById(formId);
    return control.closest ? control.closest('form') : null;
  }

  function stopCheckoutEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  function resumeCheckout(control, form) {
    if (control) state.approvedCheckoutControls.add(control);
    if (form) state.approvedCheckoutForms.add(form);

    if (control && typeof control.click === 'function') {
      control.click();
      return;
    }

    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    window.location.assign('/checkout');
  }

  async function handleCheckoutIntent(event, control, form) {
    stopCheckoutEvent(event);
    if (state.checkoutChecking) return;

    state.checkoutChecking = true;
    try {
      const cart = await fetchCart();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (
        isAddonOnlyCart(items) ||
        (cart.item_count > 0 && Number(cart.total_price) === 0) ||
        hasPaidEbook(items)
      ) {
        await enforceAddonOnlyRestriction({ cart, reload: true, stripPaidEbook: true });
      } else {
        resumeCheckout(control, form);
      }
    } catch (error) {
      console.error('GPM addon checkout guard failed:', error);
      window.alert('We could not validate your cart before checkout. Please try again.');
    } finally {
      state.checkoutChecking = false;
    }
  }

  function bindCheckoutGuards() {
    document.addEventListener(
      'click',
      function (event) {
        const control = getCheckoutControl(event.target);
        if (!control) return;
        if (state.approvedCheckoutControls.has(control)) {
          state.approvedCheckoutControls.delete(control);
          return;
        }
        handleCheckoutIntent(event, control, getControlForm(control));
      },
      true
    );

    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!form) return;
        if (state.approvedCheckoutForms.has(form)) {
          state.approvedCheckoutForms.delete(form);
          return;
        }
        const action = form.getAttribute('action') || '';
        if (action === '/cart' || action.indexOf('/cart') === 0 || form.querySelector('[name="checkout"]')) {
          const submitter = event.submitter;
          if (submitter && submitter.getAttribute('name') === 'checkout') {
            handleCheckoutIntent(event, submitter, form);
          }
        }
      },
      true
    );
  }

  function bindCartUpdateListener() {
    document.addEventListener('cart:updated', scheduleMutationCheck);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleMutationCheck);
    }
  }

  window.GPMAddonCartGuard = {
    ADDON_ONLY_PRODUCT_IDS,
    EBOOK_PRODUCT_ID,
    isAddonProductId,
    isAddonOnlyCart,
    hasPaidEbook,
    enforce: enforceAddonOnlyRestriction,
    fetchCart,
  };

  observeFetchCartMutations();
  observeXhrCartMutations();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindCheckoutGuards();
      bindCartUpdateListener();
      enforceAddonOnlyRestriction({ reload: true });
    });
  } else {
    bindCheckoutGuards();
    bindCartUpdateListener();
    enforceAddonOnlyRestriction({ reload: true });
  }
})();
